import { ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateFreeShipping } from '@/api/domains/markets';
import type { Market } from '@/api/types/services';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';

import { CountryFlags } from '../markets/components/CountryFlags';
import { FreeShippingPreview } from './FreeShippingPreview';

/**
 * One market section (Figma 712-16629): collapsible header (name + flags + chevron)
 * over a grey panel holding the free-shipping widget toggle, the storefront preview
 * (both states) and a per-market Save.
 */
export function FreeShippingMarketSection({ market }: { market: Market }) {
  const { t } = useTranslation('widgets');
  // Tolerate a stale cached market shape that predates the free_shipping field.
  const fs = market.free_shipping ?? { active: false };

  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(fs.active);

  const update = useUpdateFreeShipping(market.id);

  const handleSave = () => {
    update.mutate(
      { active },
      {
        onSuccess: () => toast.success(t('freeShipping.saved')),
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  return (
    <section>
      <div className="border-t border-[#ededed]" />
      <div className="flex items-center justify-between py-4">
        <div className="flex min-w-0 flex-col gap-[3px]">
          <h3 className="truncate text-xl font-medium tracking-[-0.6px] text-black">
            {market.name}
          </h3>
          <CountryFlags codes={market.countries} />
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? t('freeShipping.collapse') : t('freeShipping.expand')}
          className="flex h-9 w-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent"
        >
          <ChevronUp className={cn('size-6 transition-transform', !open && 'rotate-180')} />
        </button>
      </div>
      <div className="border-t border-[#ededed]" />

      {open ? (
        <div className="bg-[#f7f7f7] px-6 py-8">
          <div className="flex w-full max-w-[695px] flex-col gap-[21px]">
            <div className="flex flex-col gap-3.5">
              <h4 className="text-xl font-medium tracking-[-0.6px] text-black">
                {t('freeShipping.widgetTitle')}
              </h4>
              <div className="flex items-center gap-2.5">
                <Switch
                  checked={active}
                  onCheckedChange={setActive}
                  aria-label={t('freeShipping.toggleLabel')}
                />
                <span className="text-sm font-medium text-[#171717]">
                  {t('freeShipping.active')}
                </span>
              </div>
            </div>

            <div className="flex max-w-[469px] flex-col gap-3">
              <p className="text-sm font-medium text-[#0a0a0a]">{t('freeShipping.preview')}</p>
              <FreeShippingPreview complete />
              <FreeShippingPreview complete={false} />
            </div>

            <Button
              type="button"
              onClick={handleSave}
              disabled={update.isPending}
              className="w-fit bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t('freeShipping.save')}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
